import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Send, Trash2 } from "lucide-react";
import {
  deleteNotificationSettings,
  getNotificationSettings,
  putNotificationSettings,
  testNotificationSettings,
  type NotificationTestResult,
} from "../api/notifications";
import Card from "../components/Card";
import Button from "../components/Button";
import { apiErrorMessage } from "../lib/api";

export default function NotificationsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["notification-settings"],
    queryFn: getNotificationSettings,
  });

  const [botToken, setBotToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [testResult, setTestResult] = useState<NotificationTestResult | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Seed the form from the stored config (token stays blank — never returned).
  useEffect(() => {
    if (data) {
      setChatId(data.chat_id ?? "");
      setEnabled(data.enabled);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      putNotificationSettings({ bot_token: botToken, chat_id: chatId, enabled }),
    onSuccess: () => {
      setBotToken("");
      setSaveError(null);
      setTestResult(null);
      qc.invalidateQueries({ queryKey: ["notification-settings"] });
    },
    onError: (e) => setSaveError(apiErrorMessage(e, "Save failed")),
  });

  const remove = useMutation({
    mutationFn: deleteNotificationSettings,
    onSuccess: () => {
      setBotToken("");
      setChatId("");
      setTestResult(null);
      qc.invalidateQueries({ queryKey: ["notification-settings"] });
    },
  });

  const [testing, setTesting] = useState(false);
  async function runTest() {
    setTesting(true);
    setTestResult(null);
    try {
      setTestResult(await testNotificationSettings());
    } catch (e) {
      setTestResult({ ok: false, detail: apiErrorMessage(e) });
    } finally {
      setTesting(false);
    }
  }

  // Need a token only for the first save; afterwards it can be left blank to
  // keep the existing one (the API requires a token, so we re-require it only
  // when none is stored yet).
  const hasToken = data?.has_token ?? false;
  const canSave = chatId.trim().length > 0 && (hasToken || botToken.trim().length > 0);

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold">Notifications</h1>
        <p className="text-sm text-fg-subtle mt-1">
          Get a Telegram message on every order your deployments place (paper and live).
        </p>
      </div>

      <Card
        title="Telegram"
        subtitle={
          data?.configured
            ? `Configured${data.enabled ? "" : " (disabled)"}`
            : "Not configured"
        }
      >
        {isLoading ? (
          <div className="text-sm text-fg-subtle">Loading…</div>
        ) : (
          <div className="space-y-4">
            <label className="block">
              <span className="text-sm font-medium">Bot token</span>
              <input
                type="password"
                autoComplete="off"
                value={botToken}
                onChange={(e) => setBotToken(e.target.value)}
                placeholder={hasToken ? "•••••• (stored — leave blank to keep)" : "123456:ABC-..."}
                className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
              />
              <span className="text-xs text-fg-subtle">
                Create a bot with @BotFather and paste its token. Stored encrypted; never shown again.
              </span>
            </label>

            <label className="block">
              <span className="text-sm font-medium">Chat ID</span>
              <input
                value={chatId}
                onChange={(e) => setChatId(e.target.value)}
                placeholder="e.g. 987654321"
                className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
              />
              <span className="text-xs text-fg-subtle">
                Message your bot, then get your chat id (e.g. via @userinfobot).
              </span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
              />
              <span className="text-sm">Enabled</span>
            </label>

            {saveError && <div className="text-sm text-down-600 dark:text-down-400">{saveError}</div>}

            <div className="flex flex-wrap gap-2">
              <Button onClick={() => save.mutate()} disabled={!canSave || save.isPending}>
                {save.isPending ? "Saving…" : "Save"}
              </Button>
              <Button
                variant="outline"
                iconLeft={<Send size={15} />}
                onClick={runTest}
                disabled={!data?.configured || testing}
              >
                {testing ? "Sending…" : "Send test"}
              </Button>
              {data?.configured && (
                <Button
                  variant="danger"
                  iconLeft={<Trash2 size={15} />}
                  onClick={() => remove.mutate()}
                  disabled={remove.isPending}
                >
                  Remove
                </Button>
              )}
            </div>

            {testResult && (
              <div
                className={`text-sm ${testResult.ok ? "text-up-600 dark:text-up-400" : "text-down-600 dark:text-down-400"}`}
              >
                {testResult.ok ? "✓ " : "✗ "}
                {testResult.detail}
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
